import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Dialog, DialogTitle, IconButton, Link, Skeleton, Typography } from '@mui/material';
import { piiProps } from 'src/components/DemoBlur';
import { Iconify } from '../../../../components';
import useCustomSnackbar from '../../../../hooks/useCustomSnackbar';
import { callApi } from '../../../../utils/apiCall';
import { dispatch } from '../../../../redux/store';
import { updateError } from '../../../../redux/slices/user';
import { completeChecklistItem } from '../../../../redux/slices/taskSlice';
import { buildCompletionPayload } from '../../../../utils/checklistCompletionPayload';
import useLeadReminderTasks from '../../../../sections/@dashboard/leadDetail/hooks/useLeadReminderTasks';
import { buildStandaloneItems } from '../../../../sections/@dashboard/leadDetail/hooks/useV2ChecklistAutocomplete';
import UpNextCardV2 from '../../../../sections/@dashboard/leadDetail/AttendedGuidedChecklist/v2/UpNextCardV2';
import ActionPanel, { EMAIL_ACTION_TYPES, WHATSAPP_ACTION_TYPES } from '../../../../sections/@dashboard/leadDetail/AttendedGuidedChecklist/ActionPanel';
import { INLINE_HANDLED_TYPES } from '../../../../sections/@dashboard/leadDetail/AttendedGuidedChecklist/panels';
import { getTaskRenamePlan } from '../../../../sections/@dashboard/leadDetail/taskRenameRefreshUtils';

// -----------------------------------------------------------------------------
// NextTaskCardDialog (Task J) — replaces the TaskViewer sidebar for the Next
// Task cell. A small centred dialog containing ONLY the follow-up popup's
// "Up next" card (UpNextCardV2) for the lead's one next task — same
// component, same styling, no assignees/comments/attachments/timeline.
//
// Data: useLeadReminderTasks (the same hook ScheduleReminderTaskSection and
// FollowUpV2 use) — NOT a bespoke fetch. completeItem already routes
// standalone-task vs checklist-item completion and refreshes on success; we
// just wire it to close the dialog + notify the caller.
//
// Reschedule (onChangeDueDate) is a SCOPED single-task PATCH, not the full
// multi-task cascade ScheduleReminderTaskSection.handleChangeDueDate runs.
// That cascade shifts *other* visible tasks' due dates to keep a sequence —
// this dialog shows exactly one task and nothing else, so there is nothing
// to cascade into. getTaskRenamePlan (the same pure helper that function
// uses) still drives the standalone-vs-checklist-item / title-changed
// branching, so the two implementations can't silently diverge on *that*
// logic even though the due-date side is intentionally narrower here.
// -----------------------------------------------------------------------------
export default function NextTaskCardDialog({
    open,
    onClose,
    practiceId = undefined,
    leadId = undefined,
    taskId = undefined,
    leadName = '',
    leadEmail = '',
    onCompleted = undefined,
    row = undefined,
    onOpenContactLead = undefined,
}) {
    const { enqueueSnackbar } = useCustomSnackbar();
    const { items, refresh, completeItem } = useLeadReminderTasks({ practiceId, leadId });

    // The row only carries next_task_title/next_task_due_at — taskId is
    // resolved separately (resolveNextTaskId) before this dialog opens.
    const matchedItem = useMemo(
        () => (items || []).find((i) => i.taskId === taskId) || null,
        [items, taskId]
    );

    // 2026-10-02 fix: useV2ChecklistAutocomplete's /tasks/ fetch + checklist
    // merge can legitimately drop a real, open, top-level task that
    // resolveNextTaskId resolved — e.g. a task whose checklist-item row got
    // excluded by the current-status template filter (filterGroupsByCurrentStatus)
    // while its parent task ALSO isn't "standalone" by the hook's template_id
    // check. Previously this fell back to "first OPEN item" in `items`, which
    // silently showed a DIFFERENT real task than the one the cell/resolver
    // pointed at (prod: cell showed "Booked Pt", dialog showed "Call Sophie
    // about Other"). Never show a different task than the cell resolved —
    // instead, fetch that exact task by id and adapt it with the SAME
    // standalone-task adapter the hook uses, so the shape (taskId/itemId/
    // isStandaloneTask/etc.) UpNextCardV2 and our handlers expect is identical
    // either way.
    const [fallback, setFallback] = useState({ status: 'idle', item: null, forTaskId: null });

    // 2026-10-02 fix #2: gating the fallback fetch on the HOOK's own `loading`
    // flag caused the dialog to get stuck on the skeleton forever. NewLeadDataGrid
    // keeps NextTaskCardDialog permanently mounted (only `open` toggles), so
    // useLeadReminderTasks({ practiceId, leadId }) is ALREADY running with a
    // stale/previous leadId before the user clicks a new row. The render where
    // `leadId`/`taskId` change to the new row's values can see `loading: false`
    // (stale, from before) in the SAME tick the hook's own effect flips it to
    // `true` to start its real fetch. That transition re-ran this effect (its
    // `loading` dependency changed), which CLEANED UP the in-flight fallback
    // fetch (setting a local `cancelled` flag) but left `fallback.status`
    // stuck at 'loading' forever — the guard below then treated "status
    // loading" as "already attempted" and never retried once the hook
    // actually settled. Fix: the fallback fetch no longer depends on the
    // hook's `loading` AT ALL — it starts as soon as there's no matchedItem,
    // and a per-taskId ref (not component state, so a cancelled run can't
    // reset it) prevents it from ever being double-fetched for the same id.
    // The one tradeoff: this can fire one redundant GET in parallel with the
    // hook's own list fetch for the common case where the task WOULD have
    // turned up in `items` anyway — harmless, since matchedItem always wins
    // over the fallback item once/if it appears (see `item` below).
    const attemptedTaskIdsRef = useRef(new Set());

    // Shared fetch+adapt — used both for the initial resolve-miss below AND to
    // re-sync the fallback item's own fields (e.g. due_at) after a successful
    // Reschedule, since a fallback item never appears in `items` for refresh()
    // to update naturally.
    const fetchAndAdaptTask = async (id) => {
        try {
            const res = await callApi(`/api/v1/practices/${practiceId}/tasks/${id}/`, 'GET');
            const task = res?.status === 200 ? res.data : null;
            return task ? buildStandaloneItems([task])[0] || null : null;
        } catch {
            return null;
        }
    };

    useEffect(() => {
        if (!open) return undefined;
        if (matchedItem) return undefined; // found in the normal list — no fallback needed
        if (!taskId || !practiceId) {
            setFallback({ status: 'notfound', item: null, forTaskId: taskId });
            return undefined;
        }
        if (attemptedTaskIdsRef.current.has(taskId)) return undefined; // already fetching/fetched this id

        attemptedTaskIdsRef.current.add(taskId);
        let cancelled = false;
        setFallback({ status: 'loading', item: null, forTaskId: taskId });
        fetchAndAdaptTask(taskId).then((adapted) => {
            if (!cancelled) setFallback({ status: adapted ? 'success' : 'notfound', item: adapted, forTaskId: taskId });
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, matchedItem, taskId, practiceId]);

    const item = matchedItem || (fallback.forTaskId === taskId && fallback.status === 'success' ? fallback.item : null);
    const isFallbackItem = !matchedItem && fallback.forTaskId === taskId && fallback.status === 'success';

    // ── Embedded action panel ────────────────────────────────────────────────
    // "Log outcome & next step" etc. open the SAME ActionPanel router the
    // lead-detail checklist uses, inside this dialog, so a task can be completed
    // without the full lead view. Panels read `leadDetail` from props (not Redux),
    // so it is fetched lazily (once per lead) when a panel opens.
    const [actionPanelItem, setActionPanelItem] = useState(null);
    const [leadDetail, setLeadDetail] = useState(null);
    const [leadDetailStatus, setLeadDetailStatus] = useState('idle'); // idle | loading | done
    const leadDetailCacheRef = useRef({});
    const completingRef = useRef(new Set());

    // Reset the panel whenever the dialog closes / switches lead.
    useEffect(() => {
        if (!open) setActionPanelItem(null);
    }, [open, leadId]);

    useEffect(() => {
        if (!actionPanelItem || !practiceId || !leadId) return undefined;
        const cached = leadDetailCacheRef.current[leadId];
        if (cached) {
            setLeadDetail(cached);
            setLeadDetailStatus('done');
            return undefined;
        }
        let cancelled = false;
        setLeadDetailStatus('loading');
        callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/`, 'GET')
            .then((res) => {
                if (cancelled) return;
                const data = res?.status === 200 ? res.data : null;
                if (data) leadDetailCacheRef.current[leadId] = data;
                setLeadDetail(data);
                setLeadDetailStatus('done');
            })
            .catch(() => {
                if (cancelled) return;
                setLeadDetail(null);
                setLeadDetailStatus('done');
            });
        return () => { cancelled = true; };
    }, [actionPanelItem, practiceId, leadId]);

    const finishCompleted = () => {
        setActionPanelItem(null);
        refresh({ bustCache: true });
        if (onCompleted) onCompleted();
        onClose();
    };

    const handlePanelComplete = (completedItem) => {
        if (!completedItem?.taskId) { refresh({ bustCache: true }); return; }
        if (completedItem.completionAlreadyPersisted) { finishCompleted(); return; }
        // Standalone tasks (no checklist item) complete through the hook, exactly
        // as the card's own "Complete" button does.
        if (!completedItem.itemId) {
            completeItem(completedItem, {
                onSuccess: (result) => { if (result?.success) finishCompleted(); },
            });
            return;
        }
        const key = `${completedItem.taskId}:${completedItem.itemId}`;
        if (completingRef.current.has(key)) return; // re-entrancy guard
        completingRef.current.add(key);
        dispatch(completeChecklistItem(
            practiceId, completedItem.taskId, completedItem.itemId,
            ({ success }) => {
                completingRef.current.delete(key);
                // Failure: the thunk already raised the error snackbar — stay open so the rep can retry.
                if (success) finishCompleted();
                else refresh({ bustCache: true });
            },
            buildCompletionPayload(completedItem),
        ));
    };

    // Send panels (SMS etc.) report back with the message id; completes with
    // response_payload { channel, messageId } like the orchestrator.
    const handlePanelSendSuccess = (messageId) => {
        const sentItem = actionPanelItem;
        if (!sentItem?.taskId || !sentItem?.itemId) { setActionPanelItem(null); refresh({ bustCache: true }); return; }
        const CHANNEL_BY_TYPE = { SMS_SENT: 'sms', WHATSAPP_SENT: 'whatsapp' };
        const channel = CHANNEL_BY_TYPE[sentItem.item_type] || 'email';
        dispatch(completeChecklistItem(
            practiceId, sentItem.taskId, sentItem.itemId,
            ({ success }) => { if (success) finishCompleted(); else refresh({ bustCache: true }); },
            { channel, messageId: messageId || null },
        ));
    };

    const handleMarkComplete = (completedItem) => {
        completeItem(completedItem, {
            onSuccess: (result) => {
                if (result?.success) {
                    if (onCompleted) onCompleted();
                    onClose();
                }
            },
        });
    };

    const handleChangeDueDate = async (changedItem, newDate, newTitle) => {
        let parsedNewDate = null;
        if (newDate instanceof Date) parsedNewDate = newDate;
        else if (newDate) parsedNewDate = new Date(newDate);
        const plan = getTaskRenamePlan(changedItem, parsedNewDate, newTitle);

        if ((!parsedNewDate || Number.isNaN(parsedNewDate.getTime())) && !plan.titleChanged) {
            dispatch(updateError({ success: false, message: 'Please pick a valid date and time.' }));
            return false;
        }
        if (!plan.dueDateChanged && !plan.titleChanged) return true;
        if (!changedItem?.taskId) {
            dispatch(updateError({ success: false, message: "Due dates can't be set on this item yet." }));
            return false;
        }

        const taskPayload = {};
        if (plan.dueDateChanged) taskPayload.due_at = parsedNewDate.toISOString();
        if (plan.isStandalone && plan.titleChanged) taskPayload.title = plan.title;

        if (Object.keys(taskPayload).length > 0) {
            const taskResult = await callApi(`/api/v1/practices/${practiceId}/tasks/${changedItem.taskId}/`, 'patch', taskPayload);
            if (!taskResult?.ok) {
                dispatch(updateError({ success: false, message: "Couldn't update due dates. Please try again." }));
                return false;
            }
        }

        if (plan.isChecklistItem && plan.titleChanged) {
            const titleResult = await callApi(
                `/api/v1/practices/${practiceId}/tasks/${changedItem.taskId}/checklist/${changedItem.itemId}/`,
                'patch',
                { title: plan.title }
            );
            if (!titleResult?.ok) {
                dispatch(updateError({ success: false, message: "Due date updated but couldn't rename the task. Please try again." }));
                refresh({ bustCache: true });
                return false;
            }
        }

        let successMessage = 'Task rescheduled';
        if (plan.titleChanged && plan.dueDateChanged) successMessage = 'Task renamed and rescheduled';
        else if (plan.titleChanged) successMessage = 'Task renamed';
        dispatch(updateError({ success: true, message: successMessage }));

        refresh({ bustCache: true });
        // The hook's `items` never contained this task (that's why we're on the
        // fallback path at all) — refresh() alone won't surface the new due_at/
        // title, so re-fetch+adapt it directly to keep the card in sync.
        if (isFallbackItem) {
            fetchAndAdaptTask(changedItem.taskId).then((adapted) => {
                if (adapted) setFallback({ status: 'success', item: adapted, forTaskId: changedItem.taskId });
            });
        }
        return true;
    };

    // Hand off to the row's Contact Lead popup (composers, sidebar tabs) and close
    // this dialog so the two popups never stack.
    const openContactLeadFallback = () => {
        if (onOpenContactLead) {
            onClose();
            onOpenContactLead(row);
        } else {
            enqueueSnackbar('Open the Contact Lead popup for this action', { variant: 'info' });
        }
    };

    const handleActionClick = (clicked) => {
        if (!clicked) return;
        const t = clicked.item_type;
        // Inline types complete directly (CONFIRMATION / YES_NO_TOGGLE have real panels).
        if (INLINE_HANDLED_TYPES.has(t) && t !== 'CONFIRMATION' && t !== 'YES_NO_TOGGLE') {
            handlePanelComplete(clicked);
            return;
        }
        // Email / WhatsApp composers live in the lead-detail sidebar — not embeddable here.
        if (EMAIL_ACTION_TYPES.has(t) || WHATSAPP_ACTION_TYPES.has(t)) {
            openContactLeadFallback();
            return;
        }
        setActionPanelItem(clicked);
    };

    // FollowUpPanel's Email / WhatsApp channels ask the host to open a sidebar tab.
    const handleOpenSidebarTab = () => {
        setActionPanelItem(null);
        openContactLeadFallback();
    };

    // "No open task" shows ONLY once the fallback fetch has definitively
    // failed/resolved to nothing (status 'notfound'); every other state
    // without an item yet (hook still loading, fallback idle/loading) is a
    // skeleton, never a dead end.
    const showSkeleton = !item && fallback.status !== 'notfound';

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth='sm'
            fullWidth
            PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'text.secondary',
                    py: 1.5,
                }}
            >
                <span {...piiProps}>{leadName || 'Lead'}</span>
                <IconButton size='small' onClick={onClose} aria-label='Close'>
                    <Iconify icon='eva:close-fill' width={18} height={18} />
                </IconButton>
            </DialogTitle>
            {actionPanelItem && (
                <Box sx={{ px: 2, pb: 2 }} data-testid='next-task-action-panel'>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <IconButton size='small' onClick={() => setActionPanelItem(null)} aria-label='Back to task'>
                            <Iconify icon='eva:arrow-ios-back-fill' width={18} height={18} />
                        </IconButton>
                        <Typography variant='body2' sx={{ fontWeight: 600, minWidth: 0 }} noWrap>
                            {actionPanelItem.title || 'Task'}
                        </Typography>
                    </Box>
                    <Box sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
                        {leadDetailStatus !== 'done' ? (
                            <Skeleton data-testid='next-task-panel-skeleton' variant='rounded' height={180} />
                        ) : (
                            <ActionPanel
                                key={actionPanelItem.id ?? 'none'}
                                item={actionPanelItem}
                                practiceId={practiceId}
                                leadId={leadId}
                                leadDetail={leadDetail}
                                onClose={() => setActionPanelItem(null)}
                                onSendSuccess={handlePanelSendSuccess}
                                onComplete={handlePanelComplete}
                                handleReloadLeads={() => refresh({ bustCache: true })}
                                onOpenSidebarTab={handleOpenSidebarTab}
                                callScriptCategory='TCO'
                                hideHeader
                            />
                        )}
                    </Box>
                </Box>
            )}
            <Box sx={{ px: 2, pb: 2, display: actionPanelItem ? 'none' : 'block' }}>
                {showSkeleton && (
                    <Skeleton data-testid='next-task-card-skeleton' variant='rounded' height={180} />
                )}
                {!showSkeleton && item && (
                    <UpNextCardV2
                        item={item}
                        practiceId={practiceId}
                        leadId={leadId}
                        leadEmail={leadEmail}
                        onMarkComplete={handleMarkComplete}
                        onChangeDueDate={handleChangeDueDate}
                        onActionClick={handleActionClick}
                        onLeadTasksReassigned={() => refresh({ bustCache: true })}
                    />
                )}
                {!showSkeleton && item && onOpenContactLead && (
                    <Box sx={{ mt: 1.5, textAlign: 'center' }}>
                        <Link
                            component='button'
                            type='button'
                            variant='caption'
                            underline='hover'
                            data-testid='open-contact-lead-link'
                            onClick={openContactLeadFallback}
                            sx={{ textTransform: 'none !important' }}
                        >
                            Open Contact Lead popup
                        </Link>
                    </Box>
                )}
                {!showSkeleton && !item && (
                    <Typography variant='body2' color='text.secondary' sx={{ py: 4, textAlign: 'center' }}>
                        No open task for this lead.
                    </Typography>
                )}
            </Box>
        </Dialog>
    );
}

NextTaskCardDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    taskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadName: PropTypes.string,
    leadEmail: PropTypes.string,
    onCompleted: PropTypes.func,
    row: PropTypes.object,
    onOpenContactLead: PropTypes.func,
};
